import { useState, useRef } from 'react'
import { Wrench, Edit, Save } from 'lucide-react'
import { X as XIcon } from 'lucide-react'
import { talents, libraries } from '../../services/apiService'
import toast from 'react-hot-toast'
import { Autocomplete } from '@anyemp/ui-kit'
import SectionBlock from '../UI/SectionBlock'
import SectionHeader from '../UI/SectionHeader'

const ToolsSection = ({ talent, talentId, hasPermission, setTalent }) => {
  const [toolOptions, setToolOptions] = useState([])
  const [isEditingTools, setIsEditingTools] = useState(false)
  const [editingToolIds, setEditingToolIds] = useState([])
  const [toolSaving, setToolSaving] = useState(false)
  const [toolsLoading, setToolsLoading] = useState(false)
  const [editIdsReady, setEditIdsReady] = useState(false)
  const toolsLoadedRef = useRef(false)

  const loadToolOptions = async () => {
    if (toolsLoadedRef.current) return toolOptions
    toolsLoadedRef.current = true
    setToolsLoading(true)
    try {
      const res = await libraries.getTools()
      const list = res?.data || res
      const normalized = Array.isArray(list) ? list : list?.data || []
      setToolOptions(normalized)
      return normalized
    } catch (err) {
      console.error('Error loading tools:', err)
      toolsLoadedRef.current = false
      return []
    } finally {
      setToolsLoading(false)
    }
  }

  const openToolsEdit = () => {
    setIsEditingTools(true)
    setEditIdsReady(false)
    loadToolOptions().then(options => {
      const validIds = new Set(options.map(t => t.id))
      setEditingToolIds([...new Set((talent.tools || []).map(t => t.tool_id).filter(id => validIds.has(id)))])
      setEditIdsReady(true)
    })
  }

  const handleToolsSave = async () => {
    setToolSaving(true)
    try {
      const payload = [...new Set(editingToolIds)].map(toolId => ({ tool_id: toolId }))
      await talents.updateTalentTools(talentId, payload)
      const response = await talents.getTalentByIdWithRelations(talentId)
      if (response?.data) setTalent(response.data)
      setIsEditingTools(false)
      toast.success('Tools saved', { duration: 2000, position: 'bottom-right' })
    } catch (err) {
      toast.error('Failed to save tools', { duration: 2000, position: 'bottom-right' })
    } finally {
      setToolSaving(false)
    }
  }

  return (
    <SectionBlock id="tools">
      <SectionHeader
        icon={Wrench}
        title="Tools & Skills"
        action={!isEditingTools && hasPermission('talent-service', `${talent.talent_type}_tools`, 'update') ? (
          <button
            className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-primary-600 text-white text-xs font-medium rounded-lg hover:bg-primary-700 dark:bg-primary-500 dark:hover:bg-primary-600 transition-colors"
            onClick={openToolsEdit}
            onMouseEnter={loadToolOptions}
            onFocus={loadToolOptions}
          >
            <Edit className="h-3.5 w-3.5" /> Edit
          </button>
        ) : null}
      />
      {isEditingTools ? (
        <div className="space-y-3">
          <Autocomplete
            multiple
            clearable
            fullWidth
            value={editingToolIds.map(String)}
            onChange={(vals) => setEditingToolIds((vals || []).map(Number))}
            options={toolOptions.map(t => ({ value: String(t.id), label: t.name }))}
            placeholder={toolsLoading || !editIdsReady ? 'Loading…' : 'Select tools...'}
            disabled={toolSaving || toolsLoading || !editIdsReady}
          />
          <div className="flex gap-2">
            <button onClick={handleToolsSave} disabled={toolSaving || toolsLoading || !editIdsReady} className="inline-flex items-center gap-1 px-3 py-1.5 text-xs font-medium rounded-lg bg-primary-600 text-white hover:bg-primary-700 dark:bg-primary-500 dark:hover:bg-primary-600 transition-colors disabled:opacity-50">
              <Save className="h-3 w-3" /> Save
            </button>
            <button onClick={() => setIsEditingTools(false)} disabled={toolSaving} className="inline-flex items-center gap-1 px-3 py-1.5 text-xs font-medium rounded-lg border border-light-border-primary dark:border-dark-border-primary text-light-text-secondary dark:text-dark-text-secondary hover:bg-light-bg-secondary dark:hover:bg-dark-bg-secondary transition-colors disabled:opacity-50">
              <XIcon className="h-3 w-3" /> Cancel
            </button>
          </div>
        </div>
      ) : (talent.tools && talent.tools.length > 0) ? (
        <div className="flex flex-wrap gap-2">
          {[...new Map(talent.tools.map(t => [t.tool_id, t])).values()].map((tool) => (
            <div key={tool.id} className="flex items-center gap-2 px-3 py-1.5 border border-light-border-primary dark:border-dark-border-primary rounded-full bg-light-bg-tertiary dark:bg-dark-bg-tertiary">
              {tool.tool?.icon && (
                <img src={tool.tool.icon} alt={tool.tool.name || 'Tool'} className="w-4 h-4 rounded" onError={e => { e.target.style.display = 'none' }} />
              )}
              <span className="text-sm text-light-text-primary dark:text-dark-text-primary">{tool.tool?.name || 'Tool'}</span>
            </div>
          ))}
        </div>
      ) : (
        <p className="text-sm text-light-text-tertiary dark:text-dark-text-tertiary">No tools specified</p>
      )}
    </SectionBlock>
  )
}

export default ToolsSection
